import { Avatar, Button, Modal, Popover, Stack, Table } from '@mantine/core'
import type { ReactNode } from 'react'
import type { Bundle, Patient, Task } from '@medplum/fhirtypes'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useMedplum, useSubscription } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import {
  IconClipboardList,
  IconPencil,
  IconPlus,
  IconStethoscope,
  IconUsers,
  IconVideo,
} from '@tabler/icons-react'
import { CreateUpdatePatientModal } from '@/components/ui/CreateUpdatePatientModal'
import { UploadDocumentModal } from '@/components/ui/UploadDocumentModal'
import { PatientActivityStatus } from '@/components/ui/UserPortalStatus/PatientActivityStatus'
import { ResendInvitationButton } from '@/components/ui/UserPortalStatus/ResendInvitationButton'
import { IdentityVerifiedBadge } from '@/components/ui/UserPortalStatus/IdentityVerifiedBadge'
import { logError } from '@/utils/errorUtils'
import { calculateAge, formatDateTime, formatPatientName } from '@/utils.ts'
import { useAppFlags } from '@/hooks/useAppFlags'
import { orderTestsPathFor, referralStartPath } from '@/utils/order-tests-path'
import { usePatientSidePanel } from './PatientSidePanelContext.tsx'
import { ACTION_BUTTON_STYLE } from './patientChartActionStyle.ts'
import { ConsentStatus } from './ConsentStatus/ConsentStatus'
import { DoseSpotPrescribeControl } from './DoseSpotPrescribeControl.tsx'
import { TefcaSyncControl, type HgSyncStatus } from './TefcaSyncControl.tsx'
import { CreateLetterModal } from './CreateLetter'
import { CreateTaskAction } from './CreateTask'
import { DuplicateReviewBanner } from './DuplicateReviewBanner'

type Props = {
  patient: Patient
  /**
   * Optional right-edge inset applied to the portaled fixed header so the
   * right-aligned action buttons aren't visually covered by a peer overlay
   * (Care Coordinator side panel, Messages drawer). The PatientPage maps the
   * panel widths to this number; CallPage doesn't have those overlays and
   * leaves it undefined, so the header extends edge-to-edge.
   */
  rightOffset?: number
  // medplum.invalidateSearches alone doesn't refresh the header (read from
  // parent state) — parent must re-read the patient after a demographics edit.
  onPatientEdited?: () => void
  /**
   * Extra buttons appended to the right-hand action group. PatientPage uses
   * this for the Messages toggle; style them with `ACTION_BUTTON_STYLE` so
   * their metrics match the built-in buttons.
   */
  extraActions?: ReactNode
}

/**
 * Pre-measure FALLBACK for `--patient-header-height` — the real value is the
 * bar's measured `offsetHeight`, published by the `useLayoutEffect` +
 * ResizeObserver in the body (~65px in the no-wrap case; taller when the
 * `flex-wrap` action buttons stack on narrow viewports).
 *
 * The bar is portaled to <body> as a `position: fixed` element so it can
 * stretch the full viewport width — escaping Shell's `padding-right:
 * var(--zoom-panel-width)`. The published variable is composed with
 * `--recent-bar-height` to derive `CHART_CONTENT_TOP_VAR` in
 * `@/pages/Patient/chartTopOffset`, which the chart sidebar, right-pane
 * scroll container, and `<ZoomPanel>` read so they all anchor directly
 * below the patient header (and the optional RECENT bar).
 */
export const PATIENT_CHART_ACTION_BAR_HEIGHT_PX = 64

const getPatientInitials = (patient: Patient): string => {
  const name = patient.name?.[0]
  if (!name) return '?'
  const given = name.given?.[0]?.[0] || ''
  const family = name.family?.[0] || ''
  return `${given}${family}`.toUpperCase() || '?'
}

type HgImportSummary = {
  resourcesDownloaded: number
  resourcesSavedSuccessfully: number
  resourcesFailed: number
}

export const PatientChartActionBar = ({
  patient,
  rightOffset = 0,
  onPatientEdited,
  extraActions,
}: Props) => {
  // On the call route (`/call/:encounterId`) the doctor is already in a visit,
  // so the "New Encounter" affordance is hidden — the call surface (ZoomPanel's
  // "Join Session" pre-join) is the entry point there instead. Mirrors the
  // self-hide already done by `NewEncounterChartAction` in the chart list.
  const isCallPage = useMatch('/call/:encounterId/*')
  const location = useLocation()
  const { placeOrderV2, procedureReferralsV1 } = useAppFlags<{
    placeOrderV2?: boolean
    procedureReferralsV1?: boolean
  }>()
  const orderTestsPath = placeOrderV2 ? orderTestsPathFor(location.pathname) : null
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false)
  const savedInPatientModalRef = useRef(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isCreateLetterOpen, setIsCreateLetterOpen] = useState(false)
  // `?letterRequest=` is the bell's deep-link into a signature request (PD-1407):
  // open the letter straight into review mode for the provider who has to sign.
  const [searchParams, setSearchParams] = useSearchParams()
  // Latched rather than read live off the URL: the Documents tab auto-selects a
  // document and rewrites the query, which drops `letterRequest` while the modal is
  // open and would silently turn a review back into a blank new letter.
  const [letterRequestId, setLetterRequestId] = useState(
    () => searchParams.get('letterRequest') ?? undefined,
  )

  useEffect(() => {
    const requested = searchParams.get('letterRequest')
    if (!requested) return
    setLetterRequestId(requested)
    setIsCreateLetterOpen(true)
  }, [searchParams])

  const closeCreateLetter = () => {
    setIsCreateLetterOpen(false)
    setLetterRequestId(undefined)
    // Drop the param so closing and reopening doesn't snap back into review mode.
    if (letterRequestId) {
      const next = new URLSearchParams(searchParams)
      next.delete('letterRequest')
      setSearchParams(next, { replace: true })
    }
  }
  const [isNewEncounterOpen, setIsNewEncounterOpen] = useState(false)
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)

  const [hgSyncStatus, setHgSyncStatus] = useState<HgSyncStatus>('idle')
  const [hgSyncUpdatedAt, setHgSyncUpdatedAt] = useState<string | null>(null)
  const [historyTasks, setHistoryTasks] = useState<Task[]>([])
  const [historySummaries, setHistorySummaries] = useState<Record<string, HgImportSummary | null>>(
    {},
  )
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyNextUrl, setHistoryNextUrl] = useState<string | null>(null)
  const [isPullingFromHg, setIsPullingFromHg] = useState(false)

  const medplum = useMedplum()
  const navigate = useNavigate()
  const { openPanel, emitPanelEvent } = usePatientSidePanel()
  const isProjectAdmin = medplum.isProjectAdmin()
  // `isCallPage` (the /call/:encounterId match above) carries the encounter id.
  const encounterId = isCallPage?.params.encounterId
  // Show only the patient's primary (active) address — the first non-'old' address,
  // matching how usePatientGeocode selects the active address. Demographics still
  // lists every address; the header is a summary and must stay one line.
  // Skip active-but-blank entries: a sparse first address would otherwise win and
  // render nothing, hiding a populated one behind it.
  const primaryAddress = patient.address?.find(
    (a) => a.use !== 'old' && (a.line?.some(Boolean) || !!a.city || !!a.state || !!a.postalCode),
  )
  const primaryAddressLine = [
    primaryAddress?.line?.filter(Boolean).join(', '),
    [primaryAddress?.city, primaryAddress?.state, primaryAddress?.postalCode]
      .filter(Boolean)
      .join(', '),
  ]
    .filter(Boolean)
    .join(' · ')

  // Header name → Demographics section. Build the absolute path per route:
  // relative nav only swaps the splat's last segment on /call (see CallPage).
  const goToDemographics = () =>
    navigate(
      encounterId ? `/call/${encounterId}/demographics` : `/patients/${patient.id}/demographics`,
    )

  useSubscription(
    `Task?code=historical-import&patient=Patient/${patient.id}&_sort=-_lastUpdated&_count=1`,
    (bundle: Bundle) => {
      const task = bundle.entry?.find((item) => item.resource?.resourceType === 'Task')
        ?.resource as Task
      void fetchHgSyncStatus(task)
    },
  )

  useEffect(() => {
    void fetchHgSyncStatus()
    setHistoryTasks([])
    setHistorySummaries({})
    setHistoryNextUrl(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id])

  // Ref to the portaled header row so we can measure its true height below.
  const headerRef = useRef<HTMLDivElement>(null)

  // Publish the action-bar's ACTUAL rendered height onto <html> while this
  // component is mounted. Consumers (chart sidebar, right-pane scroll
  // container, ZoomPanel) compose this with the optional
  // `--recent-bar-height` to anchor their `top` / `height` to the bottom
  // edge of this bar. Measured (not the constant) so content reserves the
  // exact footprint — no gap in the no-wrap case, no overlap when the action
  // buttons wrap. The constant is only the pre-measure fallback. Cleared on
  // unmount so non-chart pages don't carry a stale offset.
  useLayoutEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--patient-header-height', `${PATIENT_CHART_ACTION_BAR_HEIGHT_PX}px`)
    const el = headerRef.current
    if (!el) {
      return () => root.style.removeProperty('--patient-header-height')
    }
    const publish = () => {
      root.style.setProperty('--patient-header-height', `${el.offsetHeight}px`)
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--patient-header-height')
    }
  }, [])

  useEffect(() => {
    if (!medplum.isSuperAdmin()) return
    if (hgSyncStatus !== 'in-progress') return

    const interval = setInterval(() => {
      void fetchHgSyncStatus()
    }, 5000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hgSyncStatus])

  const fetchHgSyncStatus = async (task?: Task): Promise<void> => {
    try {
      const patientRef = `Patient/${patient.id}`

      if (!task) {
        task = await medplum.searchOne(
          'Task',
          `code=historical-import&patient=${patientRef}&_sort=-_lastUpdated`,
        )
      }

      if (!task) {
        setHgSyncStatus('idle')
        setHgSyncUpdatedAt(null)
        return
      }

      switch (task.status) {
        case 'in-progress':
          setHgSyncStatus('in-progress')
          break
        case 'completed':
          setHgSyncStatus('completed')
          break
        case 'failed':
          setHgSyncStatus('failed')
          break
        default:
          setHgSyncStatus('idle')
      }

      setHgSyncUpdatedAt(task.meta?.lastUpdated ?? null)
    } catch (error) {
      logError('patient', 'Fetch HG sync status', error)
      setHgSyncStatus('idle')
    }
  }

  const fetchHgHistory = async (useNext: boolean = false): Promise<void> => {
    setHistoryLoading(true)
    try {
      const bundle =
        useNext && historyNextUrl
          ? await medplum.get<Bundle>(historyNextUrl)
          : await medplum.search(
              'Task',
              `code=historical-import&patient=Patient/${patient.id}&_sort=-_lastUpdated&_count=5`,
            )

      const tasks = (bundle.entry || [])
        .map((entry) => entry.resource)
        .filter((resource): resource is Task => resource?.resourceType === 'Task')

      setHistoryTasks((prev) => (useNext ? [...prev, ...tasks] : tasks))
      const nextLink = bundle.link?.find((link) => link.relation === 'next')?.url || null
      setHistoryNextUrl(nextLink)

      const summariesToFetch = tasks.filter((task) => {
        const taskId = task.id || ''
        if (!taskId || historySummaries[taskId] !== undefined) {
          return false
        }
        const summaryAttachment = task.input?.find(
          (item) => item.type?.text === 'summary' && item.valueAttachment?.url,
        )
        return !!summaryAttachment?.valueAttachment?.url
      })

      if (summariesToFetch.length) {
        const summaryEntries = await Promise.all(
          summariesToFetch.map(async (task) => {
            const taskId = task.id as string
            const summaryAttachment = task.input?.find(
              (item) => item.type?.text === 'summary' && item.valueAttachment?.url,
            )
            if (!summaryAttachment?.valueAttachment?.url) {
              return [taskId, null] as const
            }
            try {
              const resp = await fetch(summaryAttachment.valueAttachment.url)
              const data = (await resp.json()) as HgImportSummary
              return [taskId, data] as const
            } catch (error) {
              console.log('Failed to load HG summary attachment:', error)
              return [taskId, null] as const
            }
          }),
        )

        setHistorySummaries((prev) => {
          const next = { ...prev }
          for (const [taskId, summary] of summaryEntries) {
            next[taskId] = summary
          }
          return next
        })
      }
    } catch (error) {
      logError('patient', 'Fetch HG history', error)
    } finally {
      setHistoryLoading(false)
    }
  }

  const openHistoryModal = async (): Promise<void> => {
    setIsHistoryModalOpen(true)
    if (!historyLoading) {
      await fetchHgHistory()
      void fetchHgSyncStatus()
    }
  }

  const pullDataFromHG = async (fetchEverything: boolean = false): Promise<void> => {
    setIsPullingFromHg(true)
    const bot = await medplum.searchOne('Bot', 'name=PullResourceFromHg')
    if (!bot?.id) {
      setIsPullingFromHg(false)
      showNotification({ color: 'Red', message: 'HG Bot not found' })
      return
    }

    try {
      const currentTask = await medplum.searchOne(
        'Task',
        `patient=Patient/${patient.id}&status=in-progress`,
      )

      if (currentTask) {
        showNotification({
          color: 'orange',
          message: 'Pulling data already in progress',
        })
        setIsPullingFromHg(false)
        return
      }

      await medplum.startAsyncRequest(`${medplum.getBaseUrl()}fhir/R4/Bot/${bot.id}/$execute`, {
        method: 'POST',
        body: JSON.stringify({ patientId: patient?.id, fetchEverything }),
        headers: {
          'Content-Type': 'application/fhir+json',
        },
      })

      setIsPullingFromHg(false)
      showNotification({
        color: 'green',
        message: 'Pull data from Health Gorilla started successfully',
      })
    } catch (error) {
      setIsPullingFromHg(false)
      showNotification({ color: 'red', message: 'Pull data problem appear' })
      logError('patient', 'HG sync', error)
    }
  }

  return (
    <>
      <CreateUpdatePatientModal
        isOpen={isPatientModalOpen}
        onClose={() => {
          setIsPatientModalOpen(false)
          // Insurance edits inside the modal (e.g. "Add plan & close request") save on their own,
          // even when the patient form is cancelled; Demographics re-reads its insurance on this.
          // A cancel with nothing saved leaves the chart alone.
          if (savedInPatientModalRef.current) {
            savedInPatientModalRef.current = false
            emitPanelEvent('patient:edited')
          }
        }}
        patient={patient}
        successCallback={() => {
          savedInPatientModalRef.current = true
          medplum.invalidateSearches('Patient')
          onPatientEdited?.()
        }}
        onInsuranceChanged={() => {
          savedInPatientModalRef.current = true
        }}
      />
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        patient={patient}
        successCallback={(createdReference) =>
          emitPanelEvent('progressNotes:refresh', {
            kind: 'reference',
            reference: createdReference,
          })
        }
      />
      <CreateLetterModal
        opened={isCreateLetterOpen}
        onClose={closeCreateLetter}
        patient={patient}
        requestId={letterRequestId}
      />
      <Modal
        opened={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        title="Records Import History"
        size="lg"
      >
        <Table withColumnBorders striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Update Date</Table.Th>
              <Table.Th>Downloaded</Table.Th>
              <Table.Th>Saved</Table.Th>
              <Table.Th>Failed</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!historyTasks.length && (
              <Table.Tr>
                <Table.Td colSpan={5} className="text-center text-xs text-gray-500">
                  {historyLoading ? 'Loading...' : 'No historical imports found'}
                </Table.Td>
              </Table.Tr>
            )}
            {historyTasks.map((task, index) => {
              const taskId = task.id || `${index}`
              const summary = historySummaries[taskId]
              const updatedAt = task.meta?.lastUpdated
                ? formatDateTime(task.meta.lastUpdated, '/', false, true)
                : '-'
              const isLatest = index === 0
              let statusClassName = 'bg-gray-100 text-gray-600'
              switch (task.status) {
                case 'in-progress': {
                  statusClassName = 'bg-yellow-100 text-yellow-800'
                  break
                }
                case 'completed': {
                  statusClassName = 'bg-green-100 text-green-800'
                  break
                }
                case 'failed': {
                  statusClassName = 'bg-red-100 text-red-800'
                  break
                }
              }

              return (
                <Table.Tr
                  key={taskId}
                  className={`${statusClassName} ${isLatest ? 'text-[14px] font-semibold' : 'text-[12px]'}`}
                >
                  <Table.Td>{task.status || '-'}</Table.Td>
                  <Table.Td>{updatedAt}</Table.Td>
                  <Table.Td className="text-black">
                    {summary ? summary.resourcesDownloaded : '-'}
                  </Table.Td>
                  <Table.Td className="text-green-700">
                    {summary ? summary.resourcesSavedSuccessfully : '-'}
                  </Table.Td>
                  <Table.Td className="text-red-700">
                    {summary ? summary.resourcesFailed : '-'}
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
        <div className="mt-4 flex justify-center">
          <Button
            variant="light"
            disabled={!historyNextUrl || historyLoading}
            onClick={() => fetchHgHistory(true)}
          >
            {historyLoading ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      </Modal>
      {createPortal(
        <div
          ref={headerRef}
          /*
           * Patient header is portaled to <body> so it can stretch the FULL
           * viewport width (`100vw`) regardless of whether the chart's
           * `<main>` reserves `padding-right` for the <ZoomPanel> on the
           * /call route. ZoomPanel keeps `top: var(--chart-content-top)`
           * which already accounts for the header's published
           * `--patient-header-height`, so it cleanly sits BELOW the header
           * (no horizontal overlap).
           *
           * z-50 keeps it ABOVE the ZoomPanel (z-auto) and the
           * `<PatientSidePanel>` overlay (z-40), and BELOW the Shell header
           * (z-100) + the `<RecentPatientsBar>` (z-99) — so when scrolling
           * past the chart the global chrome stays on top.
           */
          /*
           * `width: 100vw` would extend ~15px past the visible viewport on
           * macOS/Windows because `100vw` includes the vertical scrollbar
           * width while the body's content width does not. That mismatch
           * produced a phantom horizontal scrollbar at the bottom of the
           * page. Switching to `left: 0; right: <rightOffset>px` (and
           * dropping `width`) lets the browser resolve the box against the
           * actual scrollable area instead — the bar now stretches edge to
           * edge of the visible viewport (minus the side-panel offset)
           * without overshooting.
           */
          className="fixed z-50 flex flex-wrap items-center justify-between gap-3 border-b border-[#EEEEEE] bg-white px-4 py-1.5"
          style={{
            top: `calc(64px + var(--recent-bar-height, 0px))`,
            left: 0,
            right: rightOffset,
            transition: 'right 300ms ease-out',
          }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <Avatar src={patient.photo?.[0]?.url} color="blue" radius="xl" size={48}>
              {getPatientInitials(patient)}
            </Avatar>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="cursor-pointer truncate text-base font-bold text-[#111] hover:underline"
                  onClick={goToDemographics}
                >
                  {formatPatientName(patient.name?.[0])}
                </span>
                <PatientActivityStatus patient={patient} />
                <IdentityVerifiedBadge patientId={patient.id} />
                <ResendInvitationButton patient={patient} />
              </div>
              <div className="text-light text-xs">
                {patient.gender?.charAt(0).toUpperCase()}
                {patient.gender?.slice(1)}
                {patient.birthDate && (
                  <>
                    , {formatDateTime(patient.birthDate, '/', false, true)} (
                    {calculateAge(patient.birthDate)} years)
                  </>
                )}
              </div>
              {primaryAddressLine && (
                <div className="text-light truncate text-xs">{primaryAddressLine}</div>
              )}
              {patient.id && <ConsentStatus patientId={patient.id} />}
            </div>
          </div>
          {/* Takes the space beside the patient's name (flex-1 = basis 0), so the actions stay on
              the header line and wrap within it; the whole group drops below only when fewer than
              280px are left. */}
          <div className="flex min-w-[280px] flex-1 flex-wrap items-center justify-end gap-2">
            {orderTestsPath && (
              <button
                type="button"
                onClick={() => navigate(orderTestsPath)}
                style={{
                  ...ACTION_BUTTON_STYLE,
                  background: '#e8624a',
                  borderColor: '#e8624a',
                  color: '#fff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d0533d'
                  e.currentTarget.style.borderColor = '#d0533d'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e8624a'
                  e.currentTarget.style.borderColor = '#e8624a'
                }}
              >
                <IconClipboardList size={14} stroke={2.5} />
                <span>Place Order</span>
              </button>
            )}
            {orderTestsPath && procedureReferralsV1 && (
              <button
                type="button"
                onClick={() => navigate(referralStartPath(orderTestsPath))}
                style={{
                  ...ACTION_BUTTON_STYLE,
                  background: '#fff',
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eff6ff'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff'
                }}
              >
                <IconStethoscope size={14} stroke={2.5} />
                <span>Referral</span>
              </button>
            )}
            {!isCallPage && (
              <Popover
                opened={isNewEncounterOpen}
                onChange={setIsNewEncounterOpen}
                position="bottom-end"
                shadow="md"
                withArrow
              >
                <Popover.Target>
                  <button
                    type="button"
                    onClick={() => setIsNewEncounterOpen((o) => !o)}
                    style={{
                      ...ACTION_BUTTON_STYLE,
                      background: '#fff',
                      borderColor: '#3b82f6',
                      color: '#3b82f6',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#eff6ff'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fff'
                    }}
                  >
                    New Encounter
                  </button>
                </Popover.Target>
                <Popover.Dropdown p={4}>
                  <Stack gap={2}>
                    <Button
                      variant="subtle"
                      justify="flex-start"
                      fullWidth
                      size="sm"
                      leftSection={<IconVideo size={16} />}
                      onClick={() => {
                        setIsNewEncounterOpen(false)
                        openPanel({ panelType: 'newEncounterSoap' })
                      }}
                    >
                      Virtual Visit
                    </Button>
                    <Button
                      variant="subtle"
                      justify="flex-start"
                      fullWidth
                      size="sm"
                      leftSection={<IconUsers size={16} />}
                      onClick={() => {
                        setIsNewEncounterOpen(false)
                        openPanel({ panelType: 'careCoordinator' })
                      }}
                    >
                      Care Coordinator
                    </Button>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
            )}
            <button
              type="button"
              onClick={() => setIsUploadModalOpen(true)}
              style={{
                ...ACTION_BUTTON_STYLE,
                background: '#fff',
                borderColor: '#3b82f6',
                color: '#3b82f6',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#eff6ff'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff'
              }}
            >
              <IconPlus size={14} stroke={2.5} />
              <span>Add Document</span>
            </button>
            <CreateTaskAction patient={patient} />
            <button
              type="button"
              onClick={() => setIsCreateLetterOpen(true)}
              style={{
                ...ACTION_BUTTON_STYLE,
                background: '#fff',
                borderColor: '#3b82f6',
                color: '#3b82f6',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#eff6ff'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff'
              }}
            >
              <IconPencil size={14} stroke={2.5} />
              <span>Create Letter</span>
            </button>
            <button
              type="button"
              onClick={() => setIsPatientModalOpen(true)}
              style={{
                ...ACTION_BUTTON_STYLE,
                background: '#fff',
                borderColor: '#3b82f6',
                color: '#3b82f6',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#eff6ff'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff'
              }}
            >
              Edit Patient
            </button>
            {patient.id && (
              // One Prescribe affordance for both surfaces: on /call the route's
              // encounter id rides along onto the prescription.
              <DoseSpotPrescribeControl
                patient={patient}
                encounterId={encounterId}
                onPatientEdited={onPatientEdited}
              />
            )}
            {patient.id && (
              <TefcaSyncControl
                patient={patient}
                syncStatus={hgSyncStatus}
                syncUpdatedAt={hgSyncUpdatedAt}
                isImporting={isPullingFromHg}
                isProjectAdmin={isProjectAdmin}
                onImport={(fetchEverything) => void pullDataFromHG(fetchEverything)}
                onOpenHistory={() => void openHistoryModal()}
              />
            )}
            {extraActions}
          </div>
          {/* Full-width row INSIDE the measured header, so --patient-header-height includes it. */}
          {patient.id && <DuplicateReviewBanner patientId={patient.id} />}
        </div>,
        document.body,
      )}
    </>
  )
}
